// ============================================================================
// Dialog styles — the one source for modals, drawers and confirms (Design
// Constitution §4 Modals, drawers, confirms; O01, O02, O04, O07, Q38)
// ----------------------------------------------------------------------------
// One scrim rgba(20,30,40,.32) (--scrim). Modal widths 440 / 640 / 920; a
// drawer ~480px from the right. Cards are radius 18 (§3). Titles 18px / 800
// ink; the one consequence line 14px gray. Footers are right-aligned (Cancel,
// then the green primary); Delete sits far left in danger text. Phones get a
// full-screen sheet (modals) or a bottom sheet (drawers) from index.html
// ([data-dialog-panel], [data-drawer-panel]), which also owns the motion:
// modals fade and rise 8px, drawers slide 24px, 180ms; reduced motion fades.
// ============================================================================
import type {CSSProperties} from 'react';

export type ModalSize = 'sm' | 'md' | 'lg';
export const MODAL_WIDTHS: Record<ModalSize, number> = {sm: 440, md: 640, lg: 920};
export const DRAWER_WIDTH = 480;

export const dialogScrimStyle: CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'var(--scrim)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 16,
};

export function dialogPanelStyle(width: number): CSSProperties {
  return {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    maxWidth: width,
    maxHeight: 'calc(100vh - 32px)',
    background: 'var(--surface)',
    color: 'var(--ink)',
    // R08: a modal is 22, a card is 18.
    borderRadius: 22,
    boxShadow: '0 18px 48px rgba(20,30,40,.18)',
    overflow: 'hidden',
  };
}

export const dialogHeaderStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '18px 20px 12px 24px',
};

export const dialogTitleStyle: CSSProperties = {
  flex: 1,
  margin: 0,
  fontSize: 18,
  fontWeight: 800,
  lineHeight: 1.3,
  color: 'var(--ink)',
};

export const dialogBodyStyle: CSSProperties = {
  padding: '0 24px 4px',
  overflowY: 'auto',
  fontSize: 14,
  lineHeight: 1.5,
};

export const dialogMessageStyle: CSSProperties = {
  margin: 0,
  fontSize: 14,
  lineHeight: 1.5,
  color: 'var(--text-2)',
};

export const dialogFooterStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '18px 24px 22px',
};

export const dialogCloseStyle: CSSProperties = {
  width: 44,
  height: 44,
  flexShrink: 0,
  border: 'none',
  borderRadius: 999,
  background: 'transparent',
  color: 'var(--text-2)',
  fontFamily: 'inherit',
  fontSize: 22,
  lineHeight: 1,
  cursor: 'pointer',
};

// The drawer panel: right edge, full height, ~480px.
export const drawerPanelStyle: CSSProperties = {
  position: 'fixed',
  top: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  flexDirection: 'column',
  width: DRAWER_WIDTH,
  maxWidth: '100vw',
  background: 'var(--surface)',
  color: 'var(--ink)',
  boxShadow: '-12px 0 40px rgba(20,30,40,.16)',
};
